'use client';

/**
 * PurchaseDetailsScreen Component
 *
 * Main purchase screen showing credit amount, payment method, fees, and pay button.
 *
 * Price display logic (two separate amounts, received as explicit props — no purchaseDetails dependency):
 * - Top section:  shows `creditsRequested` (the credits the user receives)
 * - Pay button:   shows `price` (fee-adjusted payment amount, passed from usePurchaseFlowCore)
 *
 * Examples:
 * - Regular $10 purchase:      top "$10.00", button "Pay ($10.00)"
 * - Discount "15 for $14.99":  top "$15.00", button "Pay ($14.99)"
 * - With fee (e.g. Apple Pay): top "$10.00", button "Pay ($10.50)"
 *
 * Layout (from Figma):
 * - Large amount display ($XX.XX) centered (creditsRequested)
 * - "Show details" link (if detailsSlot provided)
 * - Selected payment method card OR available payment options
 * - Optional custom action button (e.g., Apple Pay)
 * - "Pay ($X.XX)" primary button (fee-adjusted price)
 * - Purchase fees label (if applicable)
 *
 * Note: Header with title and close button is rendered by parent PurchaseFlow component.
 *
 * PAYG Mode Behavior:
 * - Filters out incompatible options (PayPal, Apple Pay one-time)
 * - Shows "Start Session" instead of "Pay $X.XX"
 * - Shows ineligibility message for incompatible default payment method
 *
 * Default Payment Not Supported:
 * - When default is Apple Pay/Google Pay but not supported on device
 * - Shows "Payment option not supported" label with error icon
 * - Shows "Change payment option" prompt
 * - Disables buy button
 */

import React, {
  useState, useMemo, useCallback
} from 'react';
import cn from 'classnames';
import type {
  PaymentMethod, PaymentOption, FlowMode, SupportedCurrency
} from '../types';
import { PaymentMethodCard, hasPaymentMethodError } from '../components/PaymentMethodCard';
import { getPaygIneligibilityTextKey } from '../utils/paygUtils';
import { getKindConfig } from '../config/gatewayRegistry';
import {
  PAYMENT_SOURCE_ERRORS, PURCHASE_FLOW_ICONS, PURCHASE_FLOW_TEXT_KEYS
} from '../utils/constants';
import { formatAmount } from '../utils/formatAmount';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import { usePaymentSupport } from '../hooks/usePaymentSupport';
import { useSetupAction } from '../hooks/useSetupAction';
import NativePaymentButton from '../components/NativePaymentButton';
import ErrorPopup from '../components/ErrorPopup';
import Toast from '../components/Toast';
import classes from './classes.module.scss';

// ============================================================================
// Types
// ============================================================================

interface PurchaseDetailsScreenProps {
  selectedPaymentMethod: PaymentMethod | null;
  paymentOptions: PaymentOption[];
  /** Currency to use for amount display (sign and ISO code) */
  currency: SupportedCurrency;
  /** Collapsible details slot (e.g., funds breakdown). Hidden in affiliatePromo mode. */
  detailsSlot?: React.ReactNode;
  /**
   * Slot that replaces the default "$X.XX" amount display.
   * Used by affiliatePromo mode to render <TopBanner> / <MarketingBanner>.
   */
  bannerSlot?: React.ReactNode;
  /**
   * Slot rendered below the payment method section, above the pay button.
   * Used by affiliatePromo mode to render offer expiry and "Maybe later" button.
   */
  promoFooterSlot?: React.ReactNode;
  isPayg?: boolean;
  /** Whether current purchase amount reflects minimum purchase */
  isMinPurchase?: boolean;
  loading?: boolean;
  error?: string | null;
  /** Flow mode - affects button text and UI sections */
  mode?: FlowMode;
  /** Fee-adjusted payment amount shown on the pay button (fees.totalAmount with price fallback). */
  price: number;
  /** Bonus amount (for affiliate promo button text). */
  bonus?: number;
  /** Fee description text from API (e.g. "Includes $0.50 processing fee"). */
  purchaseFeesText?: string;
  /**
   * Credits amount shown in the top amount section (creditsRequested with min purchase applied).
   * The pay button separately shows the fee-adjusted price.
   */
  creditsRequested?: number;
  /** When true, a payment action is already in progress — all interactive buttons are disabled */
  actionInProgress?: boolean;
  /** PAYG inline validation: true while validation API call is in progress */
  validating?: boolean;
  /** PAYG validation error message (shown inline for mobile, popup for desktop) */
  validationError?: string | null;
  /** Dismiss the inline validation error (mobile only) */
  onDismissValidationError?: () => void;
  onPaymentMethodClick: () => void;
  onAddPaymentMethodClick: (kind: string) => void;
  onPayClick: () => void;
  /**
   * For kinds whose processor prepares its own pay button (e.g. Google Pay —
   * see `rendersOwnPayButton`): true once that button is mounted and ready. The
   * Pay button shows a loading placeholder until then, so the user's first real
   * click lands on a ready flow and opens the sheet within the gesture window.
   */
  nativeButtonReady?: boolean;
  /** Generic setup callback from PurchaseFlow — routed through useSetupAction */
  onPerformSetup?: (kind: string) => Promise<void>;
  /** Called when payment options should be reloaded (e.g., "payment method cannot be used" error) */
  onReloadPaymentOptions?: () => void;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Check whether a payment option should be visible for the current PAYG mode.
 * Uses the gateway registry's paygVisibility + pre-resolved support map.
 */
const isOptionVisible = (kind: string, isPayg: boolean, supportMap: Record<string, boolean>): boolean => {
  const config = getKindConfig(kind);
  if (!config) return false;
  if (supportMap[kind] === false) return false;
  if (config.paygVisibility === 'paygOnly') return isPayg;
  if (config.paygVisibility === 'nonPaygOnly') return !isPayg;
  return true;
};

/** Renders the list of available payment options */
const PaymentOptionsList: React.FC<{
  options: PaymentOption[];
  isPayg: boolean;
  onAddClick: (kind: string) => void;
  /** Pre-resolved support map from usePaymentSupport hook */
  supportMap: Record<string, boolean>;
  /** When provided, excludes options matching this kind (e.g., the already-selected payment method) */
  excludeKind?: string;
  /** When true, all buttons are disabled (action already in progress) */
  disabled?: boolean;
}> = ({
  options, isPayg, onAddClick, supportMap, excludeKind, disabled = false
}) => {
  const { renderIcon, renderText } = usePurchaseRender();

  const visibleOptions = options.filter(
    (opt) => opt.available
      && (!excludeKind || opt.kind !== excludeKind)
      && isOptionVisible(opt.kind, isPayg, supportMap)
  );

  if (visibleOptions.length === 0) {
    return null;
  }

  // Separate native-button options (Apple Pay) from regular styled buttons
  const nativeOptions = visibleOptions.filter((opt) => getKindConfig(opt.kind)?.nativeButton);
  const regularOptions = visibleOptions.filter((opt) => !getKindConfig(opt.kind)?.nativeButton);

  return (
    <>
      {/* Native buttons (e.g., Apple Pay <apple-pay-button> web component) */}
      {nativeOptions.map((option) => {
        const config = getKindConfig(option.kind);
        if (!config?.nativeButton) return null;

        if (disabled) {
          return <div key={ option.kind } className={ classes.skeletonButton } />;
        }

        return (
          <NativePaymentButton
            key={ option.kind }
            kind={ config.nativeButton.kind }
            type={ config.nativeButton.type }
            onClick={ () => onAddClick(option.kind) }
            skeleton={ <div className={ classes.skeletonButton } /> }
          />
        );
      })}

      {/* Regular styled buttons */}
      {regularOptions.length > 0 && (
        <div className={ classes.paymentOptionsListContainer }>
          {regularOptions.map((option) => {
            const config = getKindConfig(option.kind);
            if (!config) return null;

            if (disabled) {
              return <div key={ option.kind } className={ classes.skeletonButton } />;
            }

            const buttonClass = config.optionButtonCssModifier
              ? cn(classes.paymentOptionButton, classes[`paymentOptionButton${ config.optionButtonCssModifier }`])
              : classes.paymentOptionButton;
            const iconClass = config.optionIconCssModifier
              ? cn(classes.paymentOptionIcon, classes[`paymentOptionIcon${ config.optionIconCssModifier }`])
              : classes.paymentOptionIcon;

            return (
              <div key={ option.kind } className={ classes.addPaymentMethodButtonContainer }>
                <button
                  type="button"
                  className={ buttonClass }
                  onClick={ () => onAddClick(option.kind) }
                  data-testid={ `purchase-details-add-option-${ option.kind }` }
                >
                  {config.optionIconKey && (
                    <span className={ iconClass }>
                      {renderIcon(config.optionIconKey)}
                    </span>
                  )}
                  {renderText(config.optionTextKey)}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/** Renders error/ineligibility message */
const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className={ classes.errorMessage }>
    {message}
  </div>
);

/** Renders "Payment option not supported" UI matching old notSupportedPaymentSource layout */
const UnsupportedPaymentCard: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { renderIcon, renderText } = usePurchaseRender();
  return (
    <button
      type="button"
      className={ classes.unsupportedContainer }
      onClick={ onClick }
      data-testid="purchase-details-unsupported-payment-button"
    >
      <span className={ classes.unsupportedMessage }>
        {renderText(PURCHASE_FLOW_TEXT_KEYS.PAYMENT_NOT_SUPPORTED_MESSAGE)}
      </span>
      <div className={ classes.unsupportedCard }>
        <div className={ classes.unsupportedCardNameContainer }>
          <span className={ classes.unsupportedCardIcon }>
            {renderIcon(PURCHASE_FLOW_ICONS.PAYMENT_METHOD_INVALID)}
          </span>
          <span className={ classes.unsupportedCardText }>
            {renderText(PURCHASE_FLOW_TEXT_KEYS.SELECT_DIFFERENT_PAYMENT_METHOD)}
          </span>
        </div>
        <span className={ classes.unsupportedCardArrow }>
          {renderIcon(PURCHASE_FLOW_ICONS.ARROW_RIGHT)}
        </span>
      </div>
    </button>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const PurchaseDetailsScreen: React.FC<PurchaseDetailsScreenProps> = ({
  selectedPaymentMethod,
  paymentOptions,
  currency,
  detailsSlot,
  bannerSlot,
  promoFooterSlot,
  isPayg = false,
  isMinPurchase = false,
  loading = false,
  error = null,
  mode = 'purchase',
  price,
  bonus = 0,
  purchaseFeesText,
  creditsRequested,
  actionInProgress = false,
  validating = false,
  validationError = null,
  onDismissValidationError,
  onPaymentMethodClick,
  onAddPaymentMethodClick,
  onPayClick,
  onPerformSetup,
  onReloadPaymentOptions,
  nativeButtonReady = false
}) => {
  const { renderIcon, renderText } = usePurchaseRender();
  const [showDetails, setShowDetails] = useState(false);

  // ============================================================================
  // Async Payment Support Resolution
  // ============================================================================

  const supportItems = selectedPaymentMethod
    ? [...paymentOptions, selectedPaymentMethod]
    : paymentOptions;
  const { supportMap, loading: supportLoading } = usePaymentSupport(supportItems);

  const noopSetup = useCallback(() => Promise.resolve(), []);
  const setup = useSetupAction(onPerformSetup || noopSetup, onAddPaymentMethodClick);

  // ============================================================================
  // Derived State
  // ============================================================================

  const displayOptions = paymentOptions;

  const isSelectedMethodPaygCompatible = !isPayg
    || !selectedPaymentMethod
    || selectedPaymentMethod.eligibleToPayAsYouGo !== false;

  const isSelectedMethodSupported = !selectedPaymentMethod
    || supportMap[selectedPaymentMethod.kind] !== false;

  const selectedMethodHasError = selectedPaymentMethod
    && hasPaymentMethodError(selectedPaymentMethod);

  const paygIneligibleTextKey = isPayg ? getPaygIneligibilityTextKey(selectedPaymentMethod) : null;
  const unsupportedMessage = (selectedPaymentMethod && supportMap[selectedPaymentMethod.kind] === false)
    ? PURCHASE_FLOW_TEXT_KEYS.PAYMENT_NOT_SUPPORTED_MESSAGE
    : null;

  // Check if payment method error blocks payment
  const isBlockingError = selectedPaymentMethod?.error
    && (selectedPaymentMethod.error === PAYMENT_SOURCE_ERRORS.CANNOT_BE_USED_ON_THIS_BROWSER
      || selectedPaymentMethod.error === PAYMENT_SOURCE_ERRORS.CANNOT_BE_USED_AT_THIS_TIME);

  // Determine if buy button should be disabled
  const shouldDisableBuyButton = actionInProgress
    || !selectedPaymentMethod
    || !isSelectedMethodPaygCompatible
    || !isSelectedMethodSupported
    || isBlockingError
    || selectedPaymentMethod.expired
    || selectedPaymentMethod.reauthorizeRequired;

  const isAffiliatePromo = mode === 'affiliatePromo';

  // Button text depends on mode:
  // - PAYG: "Start Session"
  // - Affiliate Promo: "Add $1, get $25 bonus" (matches old implementation)
  // - Default: "Pay ($X.XX)" — uses fee-adjusted price
  // NOTE: This useMemo MUST be above early returns to satisfy Rules of Hooks
  const payButtonText = useMemo(() => {
    if (isPayg) {
      return renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_START_SESSION);
    }

    if (isAffiliatePromo && bonus > 0) {
      const titleText = renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_ADD_GET_BONUS, { amount: formatAmount(price, currency.sign) });
      const bonusText = renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_ADD_GET_BONUS_SUFFIX, { amount: formatAmount(bonus, currency.sign) });
      return `${ titleText }${ bonusText }`;
    }
    return renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_PAY, { amount: formatAmount(price, currency.sign) });
  }, [isPayg, isAffiliatePromo, price, bonus, currency, renderText]);

  // ============================================================================
  // Payment Method Click Handler
  // ============================================================================

  /**
   * Handle click on selected payment method
   * Different behavior based on error state:
   * - Expired/ReauthorizeRequired/SecurityReasons: Trigger edit flow
   * - Cannot be used: Reload payment options
   * - No error / error: Navigate to payment methods list
   *
   * Note: edit routing for error methods (expired, reauthorize, security
   * reasons) is handled by PaymentMethodsListScreen, not here.
   * This matches the old flow where clicking an error method on the
   * purchase details screen opens the methods list first.
   */
  const isInteractionDisabled = actionInProgress || validating;

  const handlePaymentMethodClick = () => {
    if (!selectedPaymentMethod) {
      onPaymentMethodClick();
      return;
    }

    const { error: errorText } = selectedPaymentMethod;

    // Handle "Payment method cannot be used" - auto-reload
    if (errorText === PAYMENT_SOURCE_ERRORS.PAYMENT_METHOD_CANNOT_BE_USED) {
      onReloadPaymentOptions?.();
      return;
    }

    // All other cases (including errors) — navigate to payment methods list
    onPaymentMethodClick();
  };

  // ============================================================================
  // Loading & Error States
  // ============================================================================

  if (loading || supportLoading) {
    return (
      <div className={ cn(classes.container, classes.loadingState) } data-testid="purchase-details-loading">
        {/* Skeleton: price area — same for all platforms so holder has real height from first paint */}
        <div className={ classes.amountSection }>
          <div className={ classes.skeletonPrice } />
        </div>
        {/* Skeleton: payment button */}
        <div className={ classes.skeletonButton } />
        <div className={ classes.skeletonButton } />
      </div>
    );
  }

  if (error) {
    return (
      <div className={ classes.errorContainer } data-testid="purchase-details-error">
        <div>
          Error:
          {' '}
          {error}
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render Payment Method Section
  // ============================================================================

  const renderPaymentMethodSection = () => {
    if (isInteractionDisabled) {
      return <div className={ classes.skeletonMethodCard } />;
    }

    if (!selectedPaymentMethod || !selectedPaymentMethod.isDefault) {
      const visibleOptionsCount = displayOptions.filter((opt) => opt.available && isOptionVisible(opt.kind, isPayg, supportMap)).length;

      return (
        <>
          {visibleOptionsCount > 1 && (
            <>
              <span className={ classes.selectOptionPrompt }>
                {renderText(PURCHASE_FLOW_TEXT_KEYS.SELECT_OPTION)}
              </span>
              <div className={ classes.optionsSeparator } />
            </>
          )}
          <PaymentOptionsList
            options={ displayOptions }
            isPayg={ isPayg }
            onAddClick={ setup.handleClick }
            supportMap={ supportMap }
            disabled={ actionInProgress || setup.inProgress }
          />
          {visibleOptionsCount === 0 && (
            <button
              type="button"
              className={ classes.addPaymentButton }
              onClick={ handlePaymentMethodClick }
              data-testid="purchase-details-add-payment-button"
            >
              {renderText(PURCHASE_FLOW_TEXT_KEYS.OPTION_ADD_PAYMENT_METHOD)}
            </button>
          )}
        </>
      );
    }

    // Payment method not supported on this device
    if (unsupportedMessage) {
      return (
        <UnsupportedPaymentCard
          onClick={ handlePaymentMethodClick }
        />
      );
    }

    // Payment method with error or Payment method not eligible for PAYG — show inline warning inside the card
    if (paygIneligibleTextKey || selectedMethodHasError) {
      return (
        <PaymentMethodCard
          paymentMethod={ selectedPaymentMethod }
          onClick={ handlePaymentMethodClick }
          showError
          paygIneligibleMessage={ paygIneligibleTextKey ? renderText(paygIneligibleTextKey) : undefined }
        />
      );
    }

    // Normal payment method card (no subtitle, no continue buttons — matches old behaviour)
    return (
      <PaymentMethodCard
        paymentMethod={ selectedPaymentMethod }
        onClick={ handlePaymentMethodClick }
        showDefaultIndicator={ false }
        showError
      />
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  const renderPayButton = () => {
    if (validating || setup.inProgress) {
      return <div className={ classes.skeletonButton } />;
    }

    // Google Pay (Checkout.com Flow) prepares its button up front; show a loading
    // placeholder until it's ready so the user's first real click lands on a
    // mounted flow and opens the sheet within the gesture window (MBW-2436).
    // PAYG skips the Flow processor (no silent prep), so skip the gate.
    if (!isPayg && selectedPaymentMethod && getKindConfig(selectedPaymentMethod.kind)?.rendersOwnPayButton && !nativeButtonReady) {
      return <div className={ classes.skeletonButton } data-testid="purchase-details-pay-button-loading" />;
    }

    if (selectedPaymentMethod?.requiresConfiguration) {
      const kindConfig = getKindConfig(selectedPaymentMethod.kind);
      if (kindConfig?.nativeButton && kindConfig?.performSetup) {
        return (
          <div className={ classes.customActionButtonContainer }>
            <NativePaymentButton
              kind={ kindConfig.nativeButton.kind }
              type={ kindConfig.nativeButton.type }
              onClick={ () => setup.handleClick(selectedPaymentMethod.kind) }
              skeleton={ <div className={ classes.skeletonButton } /> }
            />
            {setup.error?.message && <ErrorMessage message={ setup.error.message } />}
          </div>
        );
      }
    }

    return (
      <button
        type="button"
        className={ cn(
          classes.payButton,
          { [classes.payButtonDisabled]: shouldDisableBuyButton }
        ) }
        onClick={ onPayClick }
        disabled={ shouldDisableBuyButton }
        data-testid="purchase-details-pay-button"
      >
        {payButtonText}
      </button>
    );
  };

  // When there's a validation error (mobile inline), hide everything except the error + OK button
  // Matches old CreditCardValidateProcessing: renderContent returns null, title is null
  if (validationError) {
    return (
      <div className={ classes.container } data-testid="purchase-details-validation-error-screen">
        <div className={ classes.validationErrorContainer } data-testid="purchase-details-validation-error-container">
          <div className={ classes.validationErrorIcon }>
            {renderIcon(PURCHASE_FLOW_ICONS.VALIDATION_ERROR)}
          </div>
          <div className={ classes.validationErrorTitle }>
            {renderText(PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_TITLE)}
          </div>
          <div className={ classes.validationErrorMessage }>
            {renderText(PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_MESSAGE)}
          </div>
        </div>
        <div className={ classes.payButtonSection }>
          <button
            type="button"
            className={ classes.payButton }
            onClick={ onDismissValidationError }
            data-testid="purchase-details-validation-error-ok-button"
          >
            {renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={ classes.container } data-testid="purchase-details-screen">
      <ErrorPopup error={ setup.error } onDismiss={ setup.resetError } />
      {/* Banner Slot: replaces the default amount display when provided */}
      {bannerSlot && (
        <div className={ classes.bannerSection }>
          {bannerSlot}
        </div>
      )}

      {/* Default Amount Display - hidden when bannerSlot is provided or in PAYG mode */}
      {/* Shows creditsRequested on top; button separately shows fee-adjusted price */}
      {!bannerSlot && !isPayg && (
        <div className={ classes.amountSection } data-testid="purchase-details-amount-section">
          <div className={ classes.amountPriceHolder }>
            <span className={ classes.amountText } data-testid="purchase-details-amount-text">
              {formatAmount(creditsRequested, currency.sign)}
              {/* Star symbol when minimum purchase applies */}
              {isMinPurchase && <span className={ classes.amountMinPurchaseStar }>*</span>}
            </span>
          </div>
          {/* Details toggle */}
          {detailsSlot && !isAffiliatePromo && (
            <>
              <button
                type="button"
                className={ classes.detailsButton }
                onClick={ () => setShowDetails(!showDetails) }
                data-testid="purchase-details-toggle-details-button"
              >
                <span className={ classes.detailsButtonText }>
                  {showDetails
                    ? renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_HIDE)
                    : renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_SHOW)}
                </span>
              </button>
              <div className={ cn(classes.detailsContentWrapper, { [classes.detailsContentOpen]: showDetails }) } data-testid="purchase-details-details-content">
                <div className={ classes.detailsContent }>
                  {detailsSlot}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Minimum purchase note — placed between amount and payment options (like old flow) */}
      {isMinPurchase && (
        <div className={ classes.minPurchaseNote }>
          <span className={ classes.minPurchaseNoteStar }>*</span>
          {renderText(PURCHASE_FLOW_TEXT_KEYS.MIN_PURCHASE_NOTE, { price: formatAmount(price, currency.sign) })}
        </div>
      )}

      {/* Payment Method Section */}
      <div className={ classes.paymentSection } data-testid="purchase-details-payment-section">
        {renderPaymentMethodSection()}
      </div>

      {/* Promo Footer Slot: expiry text, "Maybe later" button, etc. */}
      {promoFooterSlot && (
        <div className={ classes.promoFooterSection }>
          {promoFooterSlot}
        </div>
      )}

      {/* Pay Button + Toast — wrapper provides positioning context for the toast on mobile */}
      {((selectedPaymentMethod?.isDefault) || selectedPaymentMethod?.requiresConfiguration) && (
        <div className={ classes.payButtonArea }>
          <Toast />
          <div className={ classes.payButtonSection }>
            {renderPayButton()}
          </div>
        </div>
      )}

      {/* Purchase fees label (e.g., Apple Pay processing fee notice) */}
      {!isPayg && purchaseFeesText && (
        <div className={ classes.purchaseFeesLabel }>
          {purchaseFeesText}
        </div>
      )}
    </div>
  );
};

export default PurchaseDetailsScreen;
